import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, Send, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTRO = "Hello — I'm your Care Anywhere AI Booking Concierge. Tell me a bit about your event or trip and I'll help you design the perfect care plan. To start: what's the occasion, how many children, and roughly what ages?";

const PlannerChat = ({ onClose }) => {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ca_planner_session") || "");
  const [messages, setMessages] = useState([{ role: "assistant", content: INTRO }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/planner/chat`, {
        session_id: sessionId || undefined,
        message: text,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("ca_planner_session", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — our concierge is briefly unavailable. Please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div data-testid="planner-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-lg h-[90vh] sm:h-[640px] bg-background rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-primary text-primary-foreground">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full bg-primary-foreground/15 flex items-center justify-center">
              <Sparkles size={14} />
            </span>
            <div>
              <div className="font-serif-display text-base leading-none">AI Booking Concierge</div>
              <div className="text-[10px] tracking-[0.25em] uppercase opacity-80 mt-0.5">Powered by GPT-5.2</div>
            </div>
          </div>
          <button data-testid="planner-close" onClick={onClose} className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-primary-foreground/10">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-muted/20">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`planner-msg-${m.role}-${i}`}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-card border border-border rounded-bl-sm text-foreground"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-card border border-border px-4 py-2.5 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> The concierge is thinking…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-border p-3 bg-background">
          <div className="flex items-end gap-2">
            <Textarea
              data-testid="planner-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={2}
              placeholder="Tell me about your event — occasion, kids, ages, timing…"
              className="resize-none rounded-xl border-border/70 text-sm"
            />
            <Button
              data-testid="planner-send"
              onClick={send}
              disabled={busy || !input.trim()}
              className="rounded-xl h-11 w-11 p-0 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Send size={14} />
            </Button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5 text-center">
            Suggestions are guidance only. Final care plan is confirmed by a human concierge.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlannerChat;
